import React from 'react';

import type { EditorPluginOptions } from './../../ricos-common-plugins/types.js';
import type FullRicosEditor from './../../ricos-editor/tiptap/FullRicosEditor.js';
import { isEditorPlugin } from './../../ricos-editor/utils/is-editor-plugin.js';
import type { EditorPlugin } from './../../ricos-types/index.js';

// Same value as https://github.com/wix-private/wix-insiders/blob/master/packages/overrides-getters/src/cookie.ts
const STATIC_OVERRIDES_COOKIE = 'wixStaticsVersions';
const ARTIFACT_ID = 'wix-ricos';
const QUERY_PARAM_KEY = `${ARTIFACT_ID}-override`;

const getQueryParam = (key: string) => {
  if (typeof window === 'undefined') {
    return '';
  }

  const urlParams = new URLSearchParams(window.location.search);
  return decodeURIComponent(urlParams.get(key) || '');
};

const getCookie = (name: string) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  }

  return null;
};

export const getOverrideValue = (): string | null => {
  const overridesCookieValue = getCookie(STATIC_OVERRIDES_COOKIE);

  const cookiesValueMap = overridesCookieValue?.split('|').reduce(
    (acc, cookie) => {
      const [key, value] = cookie.split('#');
      return { ...acc, [key]: value };
    },
    {} as Record<string, string>
  );

  const value = getQueryParam(QUERY_PARAM_KEY) || cookiesValueMap?.[ARTIFACT_ID];

  if (!value) {
    return null;
  }

  return value;
};

const isValidHash = (hash: string) => {
  return /^[a-f0-9]+$/.test(hash);
};

const isValidVersion = (version: string) => {
  return /^\d+\.\d+\.\d+$/.test(version);
};

const getOverrideUrl = (): string | null => {
  const value = getOverrideValue();

  if (!value) {
    return null;
  }

  if (value.startsWith('http://localhost') || value.startsWith('https://localhost')) {
    const url = new URL(value);
    return `${url.href}${url.href.endsWith('/') ? '' : '/'}full-ricos-editor.js`;
  }

  if (isValidVersion(value) || isValidHash(value)) {
    return `https://static.parastorage.com/services/${ARTIFACT_ID}/${value}/full-ricos-editor.js`;
  }

  return null;
};

const wixInsiderOverrideUrl = getOverrideUrl();
export const hasWixInsiderOverride = !!wixInsiderOverrideUrl;

export const OverrideFullRicosEditorLazy = React.lazy<typeof FullRicosEditor>(async () => {
  // Here we hide the dynamic import with 'eval' from transpiler so it would not fail at build time
  // https://esbuild.github.io/content-types/#direct-eval
  // oxlint-disable-next-line no-eval,@typescript-oxlint/no-non-null-assertion
  const bundled = await (0, eval)(`import('${wixInsiderOverrideUrl!}')`);
  bundled.loadCSS();
  return { default: bundled.FullRicosEditor };
});

type LazyPluginsResolverProps = {
  plugins?: (EditorPlugin | EditorPluginOptions)[];
  children: (plugins: (EditorPlugin | EditorPluginOptions)[]) => React.ReactNode;
  hasOverride: boolean;
};

export const OverridePluginsResolver: React.FC<LazyPluginsResolverProps> = ({
  children,
  plugins,
  hasOverride,
}) => {
  const [lazyPlugins, setLazyPlugins] = React.useState<null | EditorPlugin[]>(null);

  React.useEffect(() => {
    const loadPlugins = async () => {
      // Here we hide the dynamic import with 'eval' from transpiler so it would not fail at build time
      // https://esbuild.github.io/content-types/#direct-eval
      // oxlint-disable-next-line no-eval,@typescript-oxlint/no-non-null-assertion
      const bundle = await (0, eval)(`import('${wixInsiderOverrideUrl!}')`);

      setLazyPlugins(
        plugins?.map(plugin => {
          if (isEditorPlugin(plugin)) {
            return bundle.pluginsMap[plugin.type](plugin.config);
          }
          return plugin;
        }) ?? []
      );
    };

    if (hasOverride) {
      loadPlugins();
    }
  });

  if (!hasOverride) {
    return <>{children(plugins ?? [])}</>;
  }

  if (!lazyPlugins) {
    return null;
  }

  return <>{children(lazyPlugins)}</>;
};
